import { useEffect } from "react";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { QueryClient } from "@tanstack/react-query";
import useUser from "@auth/user/useUser";
import { DEFAULT_COOKIE_EXPIRES_SECONDS } from "../cookie";
import { mustPersistQuery } from "./queryClient";

interface QueryCachePersistManagerProps {
	queryClient: QueryClient;
}

function QueryCachePersistManager({ queryClient }: QueryCachePersistManagerProps) {
	const { data: user } = useUser();
	const userId = user?.id_cad_pessoa != null ? String(user.id_cad_pessoa) : null;

	useEffect(() => {
		if (!userId) return;

		const storageKey = `SPACE_QUERY_CACHE_${userId}`;

		const persister = createAsyncStoragePersister({
			storage: window.localStorage,
			key: storageKey,
		});

		// Persister customizado que só escreve no armazenamento quando há queries para persistir.
		// Remove a chave inteiramente quando não há nada para persistir.
		const smartPersister = {
			...persister,
			persistClient: async (client: any) => {
				const hasQueries = client?.clientState?.queries?.length > 0;

				if (hasQueries) return persister.persistClient(client);

				window.localStorage.removeItem(storageKey);
			},
		};

		const [unsubscribe] = persistQueryClient({
			queryClient: queryClient,
			persister: smartPersister,
			maxAge: DEFAULT_COOKIE_EXPIRES_SECONDS.middleDay, // 12 horas
			dehydrateOptions: {
				shouldDehydrateQuery: mustPersistQuery,
			},
		});

		return () => {
			unsubscribe();
		};
	}, [userId, queryClient]);

	return null;
}

export default QueryCachePersistManager;
